import { Tooltip, TooltipTrigger, WithNAFallback } from '@toggle/design-system';
import { NumericalCellValueStyle } from '@toggle/helpers';
import { Entity, formatTicker, mapEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { PriceCellTooltip } from '~/components/price-cell/price-cell-tooltip/PriceCellTooltip';
import { getPriceStamp } from '~/components/price-display-view/helpers/price-display-helpers';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { mapValueTypeToFormatter } from '~/views/screener/results/screener-table-utils';
import { TopRankIcon } from '~/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';

import * as S from './CompetitorCard.styles';

interface CompetitorCardProps {
  entity: Entity;
  rank?: number;
}

export const CompetitorCard = ({ entity, rank }: CompetitorCardProps) => {
  const { t } = useTranslation(['widget']);
  const {
    oneDayLiveReturn,
    isSubscribing,
    date,
    isLive,
    isLivePriceSupported,
  } = useOneDayReturnLivePrice(mapEntity(entity));
  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: false,
  });

  const { navigateToPage } = useNavigateToAssetOverview();

  const formatter = mapValueTypeToFormatter['float'];
  const isNegative = !!oneDayLiveReturn && oneDayLiveReturn < 0;
  const prefix = isNegative ? '' : '+';
  const numericalStyle = isNegative
    ? NumericalCellValueStyle.Negative
    : NumericalCellValueStyle.Positive;

  if (isSubscribing) {
    return <S.LoadingSkeleton />;
  }

  return (
    <S.Root
      data-testid="competitor-card"
      onClick={() => {
        navigateToPage({ entityTag: entity.tag });
      }}
    >
      <S.IconsWrapper>
        <AssetLogoContainer entity={entity} />
        {rank && (
          <TopRankIcon
            rank={rank}
            iconSize={24}
            rankFontSize={10}
            rankTopPosition="28%"
          />
        )}
      </S.IconsWrapper>
      <S.FlexColumn>
        <S.TitleLabel data-testid={`${entity.tag}-label`}>
          {formatTicker(entity)}
        </S.TitleLabel>
        <S.SubtitleLabel>{entity.name}</S.SubtitleLabel>
      </S.FlexColumn>
      <Tooltip
        isTouchDevice={false}
        trigger={TooltipTrigger.Hover}
        label={date && <PriceCellTooltip type={stamp.stampType} date={date} />}
        inPortal
        disabled={!date || !oneDayLiveReturn}
      >
        <S.ChangePill $numericalStyle={numericalStyle}>
          <S.HorizonLabel $numericalStyle={numericalStyle}>
            {t(
              'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.oneDay'
            )}
          </S.HorizonLabel>
          <S.ChangeLabel $numericalStyle={numericalStyle}>
            <WithNAFallback
              check={oneDayLiveReturn !== undefined}
              value={
                oneDayLiveReturn !== undefined
                  ? `${prefix}${formatter(oneDayLiveReturn)}%`
                  : undefined
              }
            />
          </S.ChangeLabel>
        </S.ChangePill>
      </Tooltip>
    </S.Root>
  );
};
